// DDoS Settings Drawer — aligned with v2 design conventions.
// Swaps every Lucide import for @mui/icons-material/*Outlined and
// replaces the local `T` token bag with the shared `colors` palette
// + INDIGO / BORDER / FG* locals, matching BM Config's approach.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Modal,
  Slide,
  Stack,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  Chip,
} from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";

import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// 2026-10-04 strip: these icons stayed with the dead sections
// (SettingsSuggest for "General", Timeline for "Rate limit",
// TrendingUp for "IP surge", SmartToy for "Botnet", ReportProblem
// for "URI flood", ManageAccounts for "Session anomaly"). All six
// imports retired.
import WavesOutlinedIcon from "@mui/icons-material/WavesOutlined";

import { ReactComponent as ArrowdownIcon } from "../../../../../vendor/_new/icon/sites/arrow_down.svg";

import colors from "../../../../../utils/colors";
import { GreenSwitch, SafeTextField, GreenSelectForm } from "../../../../common/Greens";
import { SaveButton, CancelButton, formSaveButtonSx, formCancelButtonSx } from "../../../../common/Button";
import LayerdIcon from "../../../../common/LayerdIcon";
import { UI_ICONS } from "../../../../../icons/uiLucideIcons";
import { DdosSensitivity, RateLimitMitigationTimeout, UserType, ConfigAction } from "../../../../../utils/constants";
import useAuth from "../../../../../hooks/useAuth";
import useDdosConfig from "../../../../../hooks/user/useDdosConfig";
import useSite from "../../../../../hooks/user/useSite";
import { useNotification } from "../../../../../contexts/NotificationContext";
import { validateDdosSettingsForm } from "../../../../../utils/validation";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../../super/design";

// Tokens shared with DDosMigrationConfig.js
const INDIGO = colors.primary[300];
const INDIGO_BG = "rgba(99, 113, 247, 0.08)";
const INDIGO_WASH = "rgba(99, 113, 247, 0.04)";
const INDIGO_RING = "rgba(99, 113, 247, 0.22)";
const BORDER = colors.neutral[200];
const BORDER_STRONG = colors.neutral[500];
const FG1 = colors.neutral[1000];
const FG2 = colors.neutral[700];
const FG3 = colors.neutral[500];
const SUCCESS = "#1FC16B";
const SUCCESS_BG = "#E8FBEF";
const SUCCESS_FG = "#0f7a42";
const DANGER = "#FB3748";

// 2026-10-04 strip: 11 of the 14 "advanced AD threshold" fields were
// configurable in UI but had no runtime consumer — they were written
// to Postgres and read by the frozen Python AD-Engine, which hasn't
// been deployed in Atlas since the Sentinel-X migration. The columns
// stay in `ddos_configs` for rollback; the UI below drops the dead
// sections (rate_limit, ip_surge, botnet, uri_flood, session_anomaly,
// slidingWindowPiece, defaultBlockTtl) and keeps only the two
// Sentinel-X-honored fields.
//
// Fields the brain actually honors as of PR #65:
//   enabled, sensitivity, timeout         — base controls (keep in header)
//   slidingWindow, ddosVarianceThreshold  — detection knobs (keep below)
//
// Per-route detection thresholds and the stage override live in the
// two new Sentinel-X components (DDoSRouteConfigsTable, DDoSStageControl)
// — ship alongside this strip to replace what was lost.
const AD_CONFIG_GROUPS = [
  {
    label: "Entropy / variance",
    Icon: WavesOutlinedIcon,
    iconBg: SUCCESS_BG,
    iconColor: SUCCESS_FG,
    fields: [
      { key: "slidingWindow", label: "Sliding window", unit: "s", defaultVal: "30", type: "int" },
      { key: "ddosVarianceThreshold", label: "Variance threshold", unit: "σ", defaultVal: "0.05", type: "float" },
    ],
  },
];

const helperTextSx = { fontSize: 12, lineHeight: "18px", color: FG3, mx: 0, mt: 0.5 };

const menuItemSx = {
  fontSize: 14,
  lineHeight: "21px",
  "&:hover": { color: colors.primary[300] },
};
const SENSITIVITY_LABELS = { 1: "Low", 2: "Medium", 3: "High" };
const TIMEOUT_LABELS = {
  30: "30 sec",
  60: "1 min",
  600: "10 min",
  3600: "1 hour",
  86400: "1 day",
};
const TIMEOUT_ORDER = [30, 60, 600, 3600, 86400];

const SelectChevron = (props) => <ArrowdownIcon {...props} style={{ width: 20, height: 20, right: 12, top: "50%" }} />;

function Field({ label, hint, error, action, children }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0 }}>
      {(label || action) && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          {label ? (
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: FG2 }}>{label}</Typography>
          ) : (
            <Box />
          )}
          {action}
        </Stack>
      )}
      {children}
      {error ? (
        <FormHelperText error sx={helperTextSx}>{error}</FormHelperText>
      ) : hint ? (
        <Typography sx={{ fontSize: 12, lineHeight: "18px", color: FG3 }}>{hint}</Typography>
      ) : null}
    </Box>
  );
}

function SectionHeading({ title, chip }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ pt: 1, pb: 0.5 }}>
      <Typography sx={{ fontSize: 14, lineHeight: "21px", fontWeight: 500, color: FG1 }}>
        {title}
      </Typography>
      {chip}
    </Stack>
  );
}
// Segmented control — mirrors BM Config's Segmented primitive with a
// soft indigo tint on the active segment.
function Segmented({ value, onChange, options, disabled }) {
  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        background: colors.neutral[50],
        border: `1px solid ${BORDER}`,
        borderRadius: "8px",
        padding: "3px",
        gap: "2px",
        opacity: disabled ? 0.55 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      {options.map((opt) => {
        const on = opt.value === value;
        return (
          <Box
            key={opt.value}
            onClick={() => !disabled && onChange(opt.value)}
            sx={{
              flex: 1,
              textAlign: "center",
              padding: "8px 12px",
              fontSize: 14,
              lineHeight: "21px",
              fontWeight: on ? 500 : 400,
              borderRadius: "6px",
              color: on ? INDIGO : FG2,
              background: on ? "#fff" : "transparent",
              boxShadow: on ? "0 1px 2px rgba(17,17,17,0.06)" : "none",
              cursor: disabled ? "not-allowed" : "pointer",
              transition: "all 150ms",
              whiteSpace: "nowrap",
            }}
          >
            {opt.label}
          </Box>
        );
      })}
    </Box>
  );
}

function TimeoutSelect({ value, onChange, disabled, error, onBlur }) {
  return (
    <GreenSelectForm variant="outlined" fullWidth size="small" error={error} disabled={disabled}>
      <InputLabel id="ddos-mitigation-timeout">Mitigation timeout</InputLabel>
      <Select
        labelId="ddos-mitigation-timeout"
        label="Mitigation timeout"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        onClose={onBlur}
        disabled={disabled}
        IconComponent={SelectChevron}
      >
        {TIMEOUT_ORDER.map((t) => (
          <MenuItem key={t} value={t} sx={menuItemSx}>
            {TIMEOUT_LABELS[t]}
          </MenuItem>
        ))}
      </Select>
    </GreenSelectForm>
  );
}

// Threshold input — standard floating-label field with optional reset.
function ThresholdField({ field, value, showErr, errMsg, onChange, onReset, disabled, resetting }) {
  const overridden = value !== "";
  const label = field.unit ? `${field.label} (${field.unit})` : field.label;
  return (
    <Box sx={{ minWidth: 0 }}>
      {overridden && !disabled && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 0.5 }}>
          <Box
            component="button"
            type="button"
            onClick={onReset}
            disabled={resetting}
            sx={{
              border: 0,
              background: "none",
              p: 0,
              fontSize: 12,
              lineHeight: "18px",
              color: INDIGO,
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              cursor: resetting ? "not-allowed" : "pointer",
              opacity: resetting ? 0.55 : 1,
              "& svg": { width: 12, height: 12 },
            }}
          >
            {resetting ? (
              <CircularProgress size={12} sx={{ color: INDIGO }} />
            ) : (
              <RestoreOutlinedIcon />
            )}
            Reset to default
          </Box>
        </Stack>
      )}
      <SafeTextField
        fullWidth
        size="small"
        label={label}
        value={value}
        placeholder={`Default: ${field.defaultVal}`}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        sanitize={false}
        inputProps={{ inputMode: "decimal" }}
        error={showErr && !!errMsg}
        helperText={showErr && errMsg ? errMsg : undefined}
        FormHelperTextProps={{ sx: helperTextSx }}
        sx={
          overridden
            ? {
                "& .MuiOutlinedInput-root": {
                  backgroundColor: INDIGO_WASH,
                  "& fieldset": { borderColor: INDIGO },
                },
                "& .MuiInputBase-input": {
                  fontVariantNumeric: "tabular-nums",
                },
              }
            : {
                "& .MuiInputBase-input": {
                  fontVariantNumeric: "tabular-nums",
                },
              }
        }
      />
    </Box>
  );
}

const DDoSSettingsDrawer = ({ open, handleClose, siteUid, onSaved }) => {
  const { userType } = useAuth();
  const {
    ddosConfig,
    adConfig,
    getDdosConfig,
    getAdConfig,
    updateAdConfig,
    clearAdConfigOverride,
    setDdosMitigationTimeout,
    checkBrowserIntegrity,
    setDdosSensitivity,
  } = useDdosConfig();
  const { siteList, settingApply } = useSite();
  const { showSuccess, showError } = useNotification();

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [sensitivity, setSensitivity] = useState(DdosSensitivity.HIGH);
  const [ddosTimeout, setDdosTimeout] = useState(RateLimitMitigationTimeout.ONE_MINUTE);
  const [browserIntegrity, setBrowserIntegrity] = useState(false);

  const [adFormValues, setAdFormValues] = useState({});
  const [resettingKey, setResettingKey] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [baselineKey, setBaselineKey] = useState(0);

  const isReadonly = userType === UserType.ORG_USER;

  const formValidation = useMemo(
    () =>
      validateDdosSettingsForm({
        sensitivity,
        timeout: ddosTimeout,
        adFormValues,
        adConfigGroups: AD_CONFIG_GROUPS,
      }),
    [sensitivity, ddosTimeout, adFormValues]
  );

  const touchField = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const showFieldErr = useCallback(
    (field) => (submitAttempted || touched[field]) && formValidation.errors[field],
    [submitAttempted, touched, formValidation.errors]
  );

  const canSubmit = !isReadonly && !loading;

  useEffect(() => {
    if (open && siteUid) {
      setLoading(true);
      Promise.all([getDdosConfig(siteUid), getAdConfig(siteUid)])
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, siteUid, getDdosConfig, getAdConfig]);

  useEffect(() => {
    if (!open) {
      setSaving(false);
      setLoading(false);
      setResettingKey(null);
      setTouched({});
      setSubmitAttempted(false);
    }
  }, [open]);

  useEffect(() => {
    if (ddosConfig) {
      setSensitivity(ddosConfig.sensitivity);
      setDdosTimeout(ddosConfig.timeout);
      setBrowserIntegrity(!!ddosConfig.browser_integrity);
      if (open) setBaselineKey((k) => k + 1);
    }
  }, [ddosConfig, open]);

  useEffect(() => {
    if (adConfig) {
      const vals = {};
      AD_CONFIG_GROUPS.forEach((group) => {
        group.fields.forEach((f) => {
          vals[f.key] = adConfig[f.key] != null ? String(adConfig[f.key]) : "";
        });
      });
      setAdFormValues(vals);
      if (open) setBaselineKey((k) => k + 1);
    }
  }, [adConfig, open]);

  const adConfigDirty = useMemo(() => {
    if (!adConfig) return false;
    return AD_CONFIG_GROUPS.some((group) =>
      group.fields.some((f) => {
        const current = adConfig[f.key];
        const formVal = adFormValues[f.key];
        const currentStr = current != null ? String(current) : "";
        return formVal !== currentStr;
      })
    );
  }, [adConfig, adFormValues]);

  const handleAdFieldChange = (key, value) => {
    setAdFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetField = async (key) => {
    if (isReadonly) return;
    setResettingKey(key);
    const ok = await clearAdConfigOverride(siteUid, key);
    if (ok) {
      // Reflect the cleared value in the form immediately so the input
      // switches back to placeholder-with-default without waiting on the
      // network round-trip.
      setAdFormValues((prev) => ({ ...prev, [key]: "" }));
      showSuccess(`Reset "${key}" to global default`);
    }
    setResettingKey(null);
  };

  const formSnapshot = useMemo(
    () => ({ sensitivity, ddosTimeout, browserIntegrity, adFormValues }),
    [sensitivity, ddosTimeout, browserIntegrity, adFormValues],
  );
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(handleClose, isDirty, {
    disabled: saving || loading,
  });

  const handleSave = async () => {
    if (isReadonly || !siteUid) return;
    setSubmitAttempted(true);
    const validation = validateDdosSettingsForm({
      sensitivity,
      timeout: ddosTimeout,
      adFormValues,
      adConfigGroups: AD_CONFIG_GROUPS,
    });
    if (!validation.valid) return;

    setSaving(true);

    try {
      const tasks = [];

      if (ddosConfig && sensitivity !== ddosConfig.sensitivity) {
        tasks.push(() => setDdosSensitivity(siteUid, sensitivity));
      }
      if (ddosConfig && ddosTimeout !== ddosConfig.timeout) {
        tasks.push(() => setDdosMitigationTimeout(siteUid, ddosTimeout));
      }
      if (ddosConfig && browserIntegrity !== !!ddosConfig.browser_integrity) {
        tasks.push(() => checkBrowserIntegrity(siteUid, browserIntegrity));
      }

      for (const task of tasks) {
        await task();
      }

      if (adConfigDirty) {
        const payload = {};
        AD_CONFIG_GROUPS.forEach((group) => {
          group.fields.forEach((f) => {
            const formVal = (adFormValues[f.key] ?? "").trim();
            if (formVal === "") {
              payload[f.key] = null;
            } else {
              payload[f.key] = f.type === "float" ? parseFloat(formVal) : parseInt(formVal, 10);
            }
          });
        });
        const adOk = await updateAdConfig(siteUid, payload);
        if (!adOk) {
          showError("Failed to save advanced detection settings");
          setSaving(false);
          return;
        }
      }

      if (tasks.length === 0 && !adConfigDirty) {
        showSuccess("No changes to save");
        setSaving(false);
        return;
      }

      const site = siteList?.find((s) => s.id === siteUid);
      if (site) {
        const result = await settingApply(site.site_id, ConfigAction.DDOS);
        if (result && result.status !== "success") {
          showError(result.msg || "Failed to apply configuration");
          setSaving(false);
          return;
        }
      }

      showSuccess("DDoS settings saved and applied");
      setSaving(false);
      if (onSaved) onSaved();
      handleClose();
    } catch (err) {
      showError(err.message || "An error occurred while saving");
      setSaving(false);
    }
  };

  const groupHasOvr = (group) =>
    group.fields.some((f) => (adFormValues[f.key] || "").trim() !== "");

  return (
    <>
    <Modal
      open={open}
      onClose={requestClose}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" } } }}
    >
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Stack
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "33%",
            minWidth: 480,
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
            color: FG1,
          }}
        >
          {/* Head */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
            sx={{ px: 4, pt: 4, pb: 3, flexShrink: 0 }}
          >
            <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  backgroundColor: "#EEF0FE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  color: INDIGO,
                  "& svg": { width: 28, height: 28 },
                }}
              >
                <ShieldOutlinedIcon />
              </Box>
              <Stack sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: FG1 }}>
                  Edit DDoS configuration
                </Typography>
                <Typography sx={{ fontSize: 12, lineHeight: "18px", color: FG2, mt: 0.25 }}>
                  Changes are saved and applied on the next detection tick.
                </Typography>
              </Stack>
            </Stack>
            <IconButton onClick={requestClose} aria-label="Close" sx={{ p: 0 }}>
              <LayerdIcon icon={UI_ICONS.close} size={20} color="neutral" />
            </IconButton>
          </Stack>

          {/* Body */}
          <Box sx={{ flex: 1, minHeight: 0, py: 1, pl: 4 }}>
            <ReactPerfectScrollbar>
              <Box sx={{ pr: 4, pb: 2 }}>
                {loading ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                    <CircularProgress sx={{ color: INDIGO }} />
                    <Typography sx={{ fontSize: 13, color: FG2, mt: 2 }}>
                      Loading DDoS configuration…
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={4}>
                    <Stack spacing={3}>
                      <SectionHeading title="Basic configuration" />

                      <Field
                        label="Sensitivity"
                        hint="How aggressively anomalies trigger mitigation."
                        error={showFieldErr("sensitivity") ? formValidation.errors.sensitivity : null}
                      >
                        <Segmented
                          value={sensitivity}
                          onChange={(v) => {
                            setSensitivity(v);
                            touchField("sensitivity");
                          }}
                          disabled={saving || isReadonly}
                          options={[
                            { value: DdosSensitivity.LOW, label: "Low" },
                            { value: DdosSensitivity.MEIDUM, label: "Medium" },
                            { value: DdosSensitivity.HIGH, label: "High" },
                          ]}
                        />
                      </Field>

                      <Field
                        hint="How long a challenged client stays under verification."
                        error={showFieldErr("timeout") ? formValidation.errors.timeout : null}
                      >
                        <TimeoutSelect
                          value={ddosTimeout}
                          onChange={(v) => {
                            setDdosTimeout(v);
                            touchField("timeout");
                          }}
                          onBlur={() => touchField("timeout")}
                          disabled={saving || isReadonly}
                          error={Boolean(showFieldErr("timeout"))}
                        />
                      </Field>

                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={3}
                        sx={{
                          borderRadius: "8px",
                          border: `solid 1px ${colors.neutral[300]}`,
                          backgroundColor: "#fff",
                          p: 3,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                            Browser integrity check
                          </Typography>
                          <Typography sx={{ fontSize: 12, lineHeight: "18px", color: FG2, mt: 0.5 }}>
                            Validate clients with a JS challenge before passing traffic.
                          </Typography>
                        </Box>
                        <GreenSwitch
                          checked={browserIntegrity}
                          onChange={(e) => setBrowserIntegrity(e.target.checked)}
                          disabled={saving || isReadonly}
                        />
                      </Stack>
                    </Stack>

                    {AD_CONFIG_GROUPS.map((group) => {
                      const ovr = groupHasOvr(group);
                      return (
                        <Stack key={group.label} spacing={2}>
                          <SectionHeading
                            title={group.label}
                            chip={
                              ovr ? (
                                <Chip
                                  label="custom"
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    backgroundColor: INDIGO_BG,
                                    color: INDIGO,
                                    border: `1px solid ${INDIGO_RING}`,
                                  }}
                                />
                              ) : null
                            }
                          />
                          <Stack spacing={2}>
                            {group.fields.map((f) => (
                              <ThresholdField
                                key={f.key}
                                field={f}
                                value={adFormValues[f.key] ?? ""}
                                onChange={(v) => {
                                  handleAdFieldChange(f.key, v);
                                  touchField(f.key);
                                }}
                                onReset={() => handleResetField(f.key)}
                                disabled={isReadonly || saving}
                                resetting={resettingKey === f.key}
                                showErr={showFieldErr(f.key)}
                                errMsg={formValidation.errors[f.key]}
                              />
                            ))}
                          </Stack>
                        </Stack>
                      );
                    })}

                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{
                        minHeight: 48,
                        p: 2,
                        borderRadius: "8px",
                        backgroundColor: colors.primary.alpha,
                        border: `solid 1px ${colors.primary[300]}`,
                      }}
                    >
                      <InfoOutlinedIcon sx={{ width: 20, height: 20, color: INDIGO, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                        Saved and applied automatically on the next detection tick.
                      </Typography>
                    </Stack>
                  </Stack>
                )}
              </Box>
            </ReactPerfectScrollbar>
          </Box>

          {/* Foot */}
          <Box sx={{ px: 4, py: 2, borderTop: `1px solid ${BORDER}`, flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" spacing={3} sx={{ width: "100%" }}>
              <SaveButton
                onClick={handleSave}
                disabled={saving || !canSubmit}
                loading={saving}
                label="Save changes"
                sx={formSaveButtonSx}
              />
              <CancelButton
                onClick={requestClose}
                disabled={saving}
                sx={formCancelButtonSx}
              />
            </Stack>
          </Box>
        </Stack>
      </Slide>
    </Modal>
    {confirmDialog}
    </>
  );
};

export { DDoSSettingsDrawer, SENSITIVITY_LABELS, TIMEOUT_LABELS };
